import express from 'express';

import type { Auth } from '@verdaccio/auth';
import {
  antiLoop,
  encodeScopePackage,
  enforceGeneratedTokenMetadata,
  match,
} from '@verdaccio/middleware';
import type { Config } from '@verdaccio/types';

import { logger } from '../../lib/logger';
import type Storage from '../../lib/storage';
import { validateName, validatePackage } from '../../lib/validation';
import distTags from './api/dist-tags';
import pkg from './api/package';
import ping from './api/ping';
import publish from './api/publish';
import search from './api/search';
import stars from './api/stars';
import user from './api/user';
import profile from './api/v1/profile';
import v1Search from './api/v1/search';
import token from './api/v1/token';
import whoami from './api/whoami';

export default function (config: Config, auth: Auth, storage: Storage) {
  const app = express.Router();

  // validate all of these params as a package name
  // this might be too harsh, so ask if it causes trouble
  app.param('package', validatePackage);
  app.param('filename', validateName);
  app.param('tag', validateName);
  app.param('version', validateName);
  app.param('revision', validateName);
  app.param('token', validateName);

  // these can't be safely put into express url for some reason
  // TODO: For some reason? what reason?
  app.param('_rev', match(/^-rev$/));
  app.param('org_couchdb_user', match(/^org\.couchdb\.user:/));

  // Avoid executing JWT twice when the parent app already registered the JWT middleware
  const apiJwtMiddleware = auth.apiJWTmiddleware();
  app.use((req, res, next) => {
    const remoteUser = (req as any).remote_user ?? (res.locals as any).remote_user;
    if (remoteUser) {
      return next();
    }
    return apiJwtMiddleware(req, res, next);
  });
  app.use(enforceGeneratedTokenMetadata(storage, logger));
  app.use(express.json({ strict: false, limit: config.max_body_size || '10mb' }));
  app.use(antiLoop(config));
  // encode / in a scoped package name to be matched as a single parameter in routes
  app.use(encodeScopePackage);
  whoami(app);
  profile(app, auth, config);
  search(app, auth, storage);
  user(app, auth, config);
  distTags(app, auth, storage);
  publish(app, auth, storage, config);
  ping(app);
  stars(app, auth, storage, config);
  v1Search(app, auth, storage, config);
  token(app, auth, storage, config);
  pkg(app, auth, storage, config);
  return app;
}
